import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Building } from '@app/shared/models/building.interface';
import { User } from '@app/shared/models/user.interface';
import { NavigationSection } from '@app/shared/services/navigation/navigation-section';
import { BehaviorSubject, Subject, Subscription, of } from 'rxjs';
import { distinctUntilChanged, switchMap, withLatestFrom } from 'rxjs/operators';
import { NavigationService } from '@app/shared/services/navigation/navigation.service';
import { NgClass } from '@angular/common';
import { FloorService } from '@services/floor.service';
import { FeatureService } from '@services/feature.service';

@Component({
  selector: 'app-site-menu',
  templateUrl: './site-menu.component.html',
  styleUrls: ['./site-menu.component.scss'],
  imports: [NgClass]
})
export class SiteMenuComponent implements OnChanges, OnInit, OnDestroy {
  @Input() menuItems: NavigationSection[];
  @Input() user: User;
  @Input() building: Building;
  private readonly navigate$ = new Subject<NavigationSection>();

  private navigationSubscription: Subscription;

  private readonly featureService = inject(FeatureService);
  private readonly buildingId$ = new BehaviorSubject<number | null>(null);

  private readonly isRP3FeatureAvailable = toSignal(
    this.buildingId$.pipe(
      distinctUntilChanged(),
      switchMap((buildingId) =>
        buildingId == null ? of(false) : this.featureService.isAvailableForBuildingId('portal.rp3', buildingId)
      )
    ),
    { initialValue: false }
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly floorService: FloorService,
    private readonly navigationService: NavigationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['building']) {
      this.buildingId$.next(this.building?.id ?? null);
    }
  }

  navigateTo(section: NavigationSection): void {
    this.navigate$.next(section);
  }

  ngOnInit(): void {
    this.navigationSubscription = this.navigate$
      .pipe(withLatestFrom(this.route.params))
      .subscribe(([section, parameters]) => {
        const { buildingId } = parameters;
        let { floorId } = parameters;
        if (!floorId && this.isFloorAvailableInBuilding(this.floorService.currentFloorIdValue, this.building)) {
          floorId = this.floorService.currentFloorIdValue;
        }
        this.navigationService.navigateToSection(section, buildingId, floorId);
      });
  }

  ngOnDestroy(): void {
    this.navigationSubscription.unsubscribe();
  }

  isAuthorizedForMenuItemWhenBuildingHasFloors(section: NavigationSection): boolean {
    const isAuthorized = section.info.IsAuthorized(this.user, this.building.id);
    const hasFloors = this.building.floors.length > 0;
    const isNms = section.info.Id === 'nms';
    const isNmsAllowed = !isNms || (this.building.enhancedSecurity === true && this.isRP3FeatureAvailable());

    return isAuthorized && hasFloors && isNmsAllowed;
  }

  private isFloorAvailableInBuilding(floorId: number, building: Building): boolean {
    return building.floors.findIndex((f) => f.id === floorId) !== -1;
  }
}
